import React from 'react';

// --- USER & AUTH ---
export enum UserRole {
  ADMIN = 'admin',
  EMPLOYEE = 'employee',
  DEV = 'dev',
}

export interface User {
  id: string;
  name: string;
  email: string;
  password?: string;
  phone?: string;
  role: UserRole;
  avatarUrl?: string;
}

export type NewUserData = Omit<User, 'id'>;
export type UpdateUserData = Partial<NewUserData>;

// --- REMINDERS ---
export interface Reminder {
  id: string;
  projectId?: string;
  expenseId?: string;
  title: string;
  description?: string;
  dueDate: string; // YYYY-MM-DD
  isCompleted: boolean;
  createdAt?: string;
}

// --- COMPANY ---
export interface Address {
  street: string;
  number?: string;
  complement?: string;
  neighborhood: string;
  city: string;
  state: string;
  zipCode: string;
}

export interface FocalPoint {
  name: string;
  role?: string;
  email?: string;
  phone?: string;
}

export interface Company {
  id: string;
  name: string;
  cnpj?: string;
  logoUrl?: string;
  address: Address;
  phone?: string;
  email?: string;
  website?: string;
  focalPoint?: FocalPoint;
}

// --- PROJECT (OBRA) ---
export interface Project {
  id: string;
  name: string;
  companyId: string;
  personInCharge: string;
  serviceDescription: string;
  status: 'aberta' | 'fechada';
  isLocked?: boolean;
  lockedBy?: string;
  createdAt: string;
  totalCost?: number; // Added from API response
}

export type NewObraData = {
  companyId: string;
  personInCharge: string;
  serviceDescription: string;
};

export type UpdateObraData = Partial<Omit<Project, 'id' | 'name' | 'status' | 'createdAt' | 'totalCost'>>;

// --- COSTS & EXPENSES ---
export enum MainCostCategory {
  CDO = 'cdo',
  CIO = 'cio',
  DESPESAS_ADM = 'despesas_adm',
  TRIBUTOS = 'tributos',
  TRIBUTOS_INSS = 'tributos_inss',
}

export interface CostSubItem {
  id: string;
  name: string;
}

export interface CostCategoryDetail {
  id: MainCostCategory;
  name: string;
  subItems: CostSubItem[];
}

export interface Attachment {
  id: string;
  name: string;
  url: string; // base64 data URL
  type: string;
}

export interface ExpenseEntry {
  id: string;
  projectId: string;
  mainCategory: MainCostCategory;
  subCategoryItemId: string;
  subCategoryItemName: string;
  amount: number;
  date: string;
  month: string;
  description?: string;
  observation?: string;
  supplier?: string;
  invoice?: string;
  quantity?: number;
  unitPrice?: number;
  requesterId?: string;
  attachments?: Attachment[];
  // Extra fields for specific sub-items
  contract?: string;
  contact?: string;
  licensePlate?: string;
  unit?: 'peça' | 'galão' | 'quilos' | 'metros' | 'unidade';
  // Recurring expenses
  isRecurring?: boolean;
  recurrenceMonths?: number;
  // Installments
  isInstallment?: boolean;
  installmentMonths?: number;
  // Rateio
  rateioId?: string;
  // Grouping series
  seriesId?: string;
  // For sorting, from API
  createdAt?: string;
}

export interface ProjectCosts {
  [MainCostCategory.CDO]: number;
  [MainCostCategory.CIO]: number;
  [MainCostCategory.DESPESAS_ADM]: number;
  [MainCostCategory.TRIBUTOS]: number;
  [MainCostCategory.TRIBUTOS_INSS]: number;
  total: number;
}


// --- UI & CONFIG ---
export interface NavigationItemType {
  name: string;
  path: string;
  icon: React.FC<{ className?: string }>;
  allowedRoles: UserRole[];
}

export interface FormFieldConfig {
    name: keyof ExpenseEntry | 'attachments' | 'isRecurring' | 'recurrenceMonths' | 'isInstallment' | 'installmentMonths';
    label: string;
    type: 'text' | 'number' | 'date' | 'month' | 'textarea' | 'select_requester' | 'select_unit' | 'attachments' | 'amount' | 'boolean';
    required: boolean;
    gridCols: 1 | 2 | 3;
}

export interface TableColumnConfig {
    key: keyof ExpenseEntry | 'actions';
    header: string;
    render?: (value: any, entry: ExpenseEntry) => React.ReactNode;
}

export interface SubItemConfig {
    formFields: FormFieldConfig[];
    tableColumns: TableColumnConfig[];
}

// --- CHARTS ---
export interface PieChartSegment {
  name: string;
  value: number;
  percentage: number;
  color: string;
}

// --- NOTIFICATIONS ---
export interface MissingEntriesPayload {
  items: string[];
  projectName?: string;
  projectLocation?: string;
}
export interface Notification {
  id: string;
  read: boolean;
  createdAt: string;
  message: string;
  type: 'missing_entries';
  payload: MissingEntriesPayload;
}

// --- REPORT ---
export interface ReportSubItem {
    name: string;
    total: number;
}
export interface ReportCategoryGroup {
    id: MainCostCategory;
    name: string;
    total: number;
    subItems: ReportSubItem[];
}
